
// frontend/src/components/Sports/NewsGrid.js
// ENHANCED: Load More Pagination + SEO-Optimized Structure
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSportsNews } from '../../services/SportsDataService';
import { ExternalLink, Clock, Newspaper, ChevronDown, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ARTICLES_PER_PAGE = 8;

const NewsCard = ({ item, index }) => {
    const timeAgo = item.isoDate
        ? formatDistanceToNow(new Date(item.isoDate), { addSuffix: true })
        : '';

    // Placeholder image for articles without images
    const imageUrl = item.image || `https://picsum.photos/seed/${encodeURIComponent(item.title?.substring(0, 20) || index)}/400/200`;

    return (
        <motion.a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: (index % ARTICLES_PER_PAGE) * 0.05 }}
            whileHover={{ y: -4, scale: 1.02 }}
            className="group block h-full rounded-2xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-primary/30 hover:shadow-[0_0_30px_rgba(var(--primary),0.1)]"
            style={{
                background: 'rgba(255, 255, 255, 0.02)',
                backdropFilter: 'blur(12px)',
            }}
            // SEO: Schema.org Article markup
            itemScope
            itemType="https://schema.org/NewsArticle"
        >
            {/* Image */}
            <div className="relative h-40 overflow-hidden">
                <img
                    src={imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    itemProp="image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Source Badge */}
                <div className="absolute bottom-3 left-3">
                    <span
                        className="inline-flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-md text-[10px] font-bold uppercase tracking-wider text-white"
                        itemProp="publisher"
                    >
                        {item.source}
                    </span>
                </div>
            </div>

            {/* Content */}
            <div className="p-5 flex flex-col">

                {timeAgo && (
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mb-2">
                        <Clock className="w-3 h-3" />
                        <time itemProp="datePublished" dateTime={item.isoDate}>{timeAgo}</time>
                    </div>
                )}

                {/* Title - SEO: h4 with itemProp */}
                <h4
                    className="text-sm font-bold text-white leading-tight group-hover:text-primary transition-colors line-clamp-2 mb-3"
                    itemProp="headline"
                >
                    {item.title}
                </h4>

                {/* Snippet */}
                {item.contentSnippet && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-auto" itemProp="description">
                        {item.contentSnippet}
                    </p>
                )}

                {/* Read More */}
                <div className="flex items-center gap-1 text-xs text-primary font-medium mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Read More</span>
                    <ExternalLink className="w-3 h-3" />
                </div>
            </div>
        </motion.a>
    );
};

export const NewsGrid = ({ category, initialCount = 8, showLoadMore = true, showHeader = true }) => {
    const { news, isLoading, isError } = useSportsNews(category);
    const [visibleCount, setVisibleCount] = useState(initialCount);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const handleLoadMore = () => {
        setIsLoadingMore(true);
        // Simulate loading delay for smooth UX
        setTimeout(() => {
            setVisibleCount(prev => Math.min(prev + ARTICLES_PER_PAGE, news?.length || 0));
            setIsLoadingMore(false);
        }, 300);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full" />
                    <h3 className="text-xl font-bold text-white">Latest Headlines</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="h-64 rounded-2xl bg-white/5 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-8 text-center rounded-2xl bg-red-500/10 border border-red-500/20">
                <p className="text-red-400 font-medium">Failed to load news.</p>
            </div>
        );
    }

    if (!news || news.length === 0) {
        return (
            <div className="p-8 text-center rounded-2xl bg-white/5 border border-white/10">
                <Newspaper className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No news available right now.</p>
            </div>
        );
    }

    const visibleNews = news.slice(0, visibleCount);
    const hasMore = visibleCount < news.length;
    const remainingCount = news.length - visibleCount;

    return (
        <section className="space-y-6" aria-label="Sports News Headlines">
            {/* Header - SEO: Semantic heading */}
            {showHeader && (
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full" />
                        <h3 className="text-xl font-bold text-white">Latest Headlines</h3>
                        <span className="text-xs text-muted-foreground bg-white/5 px-2 py-1 rounded-full">
                            {news.length} articles
                        </span>
                    </div>
                </div>
            )}

            {/* News Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <AnimatePresence>
                    {visibleNews.map((item, index) => (
                        <NewsCard key={`${item.title}-${index}`} item={item} index={index} />
                    ))}
                </AnimatePresence>
            </div>

            {/* Load More Button */}
            {showLoadMore && hasMore && (
                <div className="flex justify-center pt-4">
                    <motion.button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="group flex items-center gap-2 px-6 py-3 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-primary/30 transition-all text-white font-medium"
                    >
                        {isLoadingMore ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Loading...</span>
                            </>
                        ) : (
                            <>
                                <span>Load More</span>
                                <span className="text-xs text-muted-foreground">({remainingCount} more)</span>
                                <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                            </>
                        )}
                    </motion.button>
                </div>
            )}

            {/* Show All / Collapse */}
            {showLoadMore && !hasMore && news.length > initialCount && (
                <div className="flex justify-center pt-4">
                    <button
                        onClick={() => setVisibleCount(initialCount)}
                        className="text-sm text-muted-foreground hover:text-white transition-colors"
                    >
                        Show Less
                    </button>
                </div>
            )}
        </section>
    );
};
